import { createContext, useContext, useEffect, useMemo, useState, useRef } from 'react'
import { useQueries } from 'react-query'

import Grid from '@mui/material/Grid'
import Avatar from '@mui/material/Avatar'
import Typography from '@mui/material/Typography'
import LinearProgress from '@mui/material/LinearProgress'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Chip from '@mui/material/Chip'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import CircularProgress from '@mui/material/CircularProgress'
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Security as SecurityIcon,
  Circle as CircleIcon,
  Info as InfoIcon,
  Business as BusinessIcon,
  NavigateNext as NavigateNextIcon,
  LockReset as LockResetIcon,
  Block as BlockIcon,
  ContentCopy as ContentCopyIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  LocalShipping as LocalShippingIcon,
} from '@mui/icons-material'


import { AddButton } from '../../../../components/global/Button'
import ActionPaper from '../../../../components/global/Widget/ActionPaper'
import PaperTitle from '../../../../components/global/Widget/ActionPaper/PaperTitle'
import { GroupPanel, GroupPanelItem, TabsPanel } from '../../../../components/global/Widget';
import UserMemberManage, { IMemberGroupFieldValue, IPermissionData } from '../../../../components/pages/Setting/Access/User/UserMemberManage'
import UserAccessManage from '../../../../components/pages/Setting/Access/User/UserAccessManage'
import UserInformation, { IUserInformationFormValues } from '../../../../components/pages/Setting/Access/User/UserInformation'
import TransportManagerDcScopeManage from '../../../../components/pages/Setting/Access/User/TransportManagerDcScopeManage'
import CustomerCompanyScopeManage from '../../../../components/pages/Setting/Access/User/CustomerCompanyScopeManage'
import SuperAdminAccessPanel, { hasAutomaticAdminAccess } from '../../../../components/pages/Setting/Access/User/SuperAdminAccessPanel'
import {
  getCustomerCompanyScopeSummaryLabel,
  shouldShowCustomerCompanyScopeSummary,
} from '../../../../components/pages/Setting/Access/User/customerCompanyScopeRules'
import { IPermission } from '../../../../compiler/global.interface'
import { useGetAllUsers, useGetUserPermissionByUUID } from '../../../../hook/query/userCRUD'
import { yupResolver } from '@hookform/resolvers/yup'
import { object, string } from 'yup'
import { EHelperText } from '../../../../utilitie/validation'
import { useForm } from 'react-hook-form'
import { useGetMemberGroupByUUID } from '../../../../hook/query/groupCRUD'
import { APIGetGroupPermissionByUUID } from '../../../../service/api/APIGroupCRUD'
import {
  buildPermissionInheritanceMap,
  countEffectivePermissionRoles,
} from '../../../../components/pages/Setting/Access/User/permissionInheritance'

export interface ISettingAccessUserPageProps {
}

export interface IUserFormValues {
  id: number | null,
  avatar: string,
  uuid: string | null,
  demo_name?: string,
  username: string,
	  firstname: string,
	  lastname: string,
	  email: string
	  user_group: any[],
	  permissionData: IPermissionData[],
	  total_customer_companies?: number,
	  is_active: number,
	  user_role_type?: string,
	}

export const DUserFormValues = {
  id: null,
  avatar: "",
  uuid: null,
  demo_name: 'สร้างผู้ใช้ใหม่',
  username: '',
  firstname: '',
  lastname: '',
  email: '',
  user_group: [],
  permissionData: [],
  is_active: 1
}

type TDefaultValues = {
  useInfoForm: {},
  useGroupForm: { group: IMemberGroupFieldValue[] },
  usePermissionForm: { permissionData: IPermission[] }
}

export interface ISettingAccessUserPageContext {
  uuid: string | null,
  setActionLog: React.Dispatch<React.SetStateAction<string>>,
  defaultValues: TDefaultValues
}

export const SettingAccessUserPageContext = createContext<Partial<ISettingAccessUserPageContext>>({})

export const useSettingAccessUserPageContextContext = () => {
  let context = useContext(SettingAccessUserPageContext) as ISettingAccessUserPageContext
  if (!context) {
    throw new Error('can not useSettingAccessUserPageContext outside SettingAccessUserPageContext')
  }
  return context
}

export default function SettingAccessUserPage(props: ISettingAccessUserPageProps) {

  const timeoutRef: { current: NodeJS.Timeout | null } = useRef(null)

  const [currentTab, setCurrentTab] = useState(0)
  const [selectedUser, setSelectedUser] = useState<IUserFormValues | null>(null)
  const [actionLog, setActionLog] = useState("none")
  const [userList, setUserList] = useState<IUserFormValues[] | []>([])
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState<string>("all")

  const [defaultValues, setDefaultValues] = useState<TDefaultValues>({
    useInfoForm: {},
    useGroupForm: { group: [] },
    usePermissionForm: { permissionData: [] }
  })

  const usersQuery = useGetAllUsers()
  const { data: usersData } = usersQuery
  const { data: userGroupData , isLoading:userGroupLoading,isFetching:userGroupFetching } = useGetMemberGroupByUUID(selectedUser?.uuid)
  const { data: userPermissionData , isLoading:userPermissionLoading, isFetching:userPermissionFetching} = useGetUserPermissionByUUID(selectedUser?.uuid)
  const selectedGroupRefs = useMemo(
    () =>
      (userGroupData || [])
        .map((membership) => ({
          uuid: membership.group_master?.uuid,
          name: membership.group_master?.name || 'กลุ่มผู้ใช้งาน',
        }))
        .filter((group) => !!group.uuid),
    [userGroupData],
  )
  const inheritedPermissionQueries = useQueries(
    selectedGroupRefs.map((group) => ({
      queryKey: ['GetGroupPermissionByUUID', selectedUser?.uuid, group.uuid],
      queryFn: () => APIGetGroupPermissionByUUID(group.uuid),
      enabled: !!selectedUser?.uuid && !!group.uuid,
      staleTime: 30000,
    })),
  )
  const inheritedPermissions = useMemo(
    () =>
      buildPermissionInheritanceMap(
        selectedGroupRefs.flatMap((group, index) => {
          const data = inheritedPermissionQueries[index]?.data as
            | { permissionData?: IPermission[] }
            | undefined
          return data?.permissionData
            ? [{ groupUuid: group.uuid, groupName: group.name, permissionData: data.permissionData }]
            : []
        }),
      ),
    [selectedGroupRefs, inheritedPermissionQueries],
  )
  const isInheritedLoading = inheritedPermissionQueries.some(
    (query) => query.isLoading || query.isFetching,
  )

  const handleSelectedUser = (user: IUserFormValues) => {
    setSelectedUser(user)
  }

  // Filter users based on search and status
  const filteredUsers = userList.filter(user => {
    const matchesSearch = !searchTerm || 
      user.firstname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username?.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesStatus = statusFilter === "all" || 
      (statusFilter === "active" && user.is_active === 1) ||
      (statusFilter === "inactive" && user.is_active === 0)

    return matchesSearch && matchesStatus
  })

  const useInfoForm = useForm<IUserInformationFormValues>({
    mode: 'all',
    defaultValues: DUserFormValues,
    resolver: yupResolver(object().shape({
      avatar: string().notRequired(),
      password:string().when('uuid',{
        is:uuid => !uuid,
        then:string().required(EHelperText.REQUIRED).notAllowSpace().min(6,EHelperText.MIN + 6).matches(/^(?=.*\d)/,EHelperText.MIN + 1 + ' ' + EHelperText.NUMERIC).matches(/[a-zA-Z]/,EHelperText.MIN + 1 + ' ' + EHelperText.LETTER),
        otherwise:string().notRequired()
      }),
      username: string()
        .required(EHelperText.REQUIRED)
        .notEmpty()
        .matches(/^[a-zA-Z0-9]+$/, 'ชื่อผู้ใช้งานต้องเป็นภาษาอังกฤษและตัวเลขเท่านั้น'),
      firstname: string().required(EHelperText.REQUIRED).notEmpty(),
      lastname: string().required(EHelperText.REQUIRED).notEmpty(),
      email: string().email(EHelperText.PATTERN).required(EHelperText.REQUIRED).notEmpty(),
    }))
  })

  const useGroupForm = useForm<{ group: IMemberGroupFieldValue[] }>({
    defaultValues: {
      group: []
    }
  })

  const usePermissionForm = useForm<{ permissionData: IPermission[] }>({
    mode: 'onSubmit',
    defaultValues: {
      permissionData: []
    }
  })

  const handleAddUser = () => {
    setActionLog('create')
    if (userList?.every(item => item.uuid)) {
      setUserList(prev => ([...prev, { ...DUserFormValues }]))
    } else {
      setSelectedUser(prev => userList[userList.length - 1])
    }
    let div = document.getElementById("GroupPanelScroll");
    timeoutRef.current && clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => {
      if (div) {
        div.scrollTop = div.scrollHeight - div.clientHeight + 56;
      }
    }, 100)
    setCurrentTab(0)
  }

  useEffect(() => {
    if (usersData) {
      setUserList(prev => {
        if (prev?.find(item => item.demo_name) && ['delete', 'edit'].includes(actionLog)) {
          return [...usersData, ...prev.filter(item => item.demo_name)]
        }
        return [...usersData]
      })
    }
  }, [usersData])

  useEffect(() => {
    if (userList) {
      switch (actionLog) {
        case 'create':
          return setSelectedUser(prev => userList[userList.length - 1])
        case 'edit':
          return () => { }
        case 'cancel-create':
          setUserList(prev => userList?.filter(item => !item.demo_name))
          return setActionLog('create')
        case 'delete':
          return setSelectedUser(userList[0])
        default:
          return setSelectedUser(userList[0])
      }
    }
  }, [userList, actionLog])

  useEffect(() => {
    if (selectedUser) {
      let resetUserInforForm = {
        avatar: selectedUser?.avatar,
        username: selectedUser?.username,
        firstname: selectedUser?.firstname,
        lastname: selectedUser?.lastname,
        email: selectedUser?.email,
        uuid: selectedUser?.uuid,
        is_active: selectedUser?.is_active
      }

      defaultValues.useInfoForm = resetUserInforForm
      useInfoForm.reset(resetUserInforForm)
    }
  }, [selectedUser])

  useEffect(()=>{
    if(userGroupData){
      let userGroupDataFiltered = userGroupData.map(group => ({
        id: group.group_master?.id,
        name: group.group_master?.name,
        option: group.group_master?.name,
        group_master_uuid: group.group_master?.uuid,
        value: group.group_master?.uuid,
        is_remove: 0
      }))

      let resetUseMemberForm = {
        group: userGroupDataFiltered
      }

      defaultValues.useGroupForm = resetUseMemberForm
      useGroupForm.reset(resetUseMemberForm)
    }
  },[userGroupData])

  useEffect(() => {
    if(userPermissionData){
      let resetUsePermissionForm = {
        permissionData: userPermissionData?.permissionData as IPermission[]
      }
      
      defaultValues.usePermissionForm = resetUsePermissionForm
      usePermissionForm.reset(resetUsePermissionForm)
    }
  }, [userPermissionData])

  const isLoadingContent = !!(userGroupLoading || userPermissionLoading || userGroupFetching || userPermissionFetching)

  // Calculate summary stats
  const summaryStats = {
    total: userList?.length || 0,
    active: userList?.filter(u => u.is_active === 1)?.length || 0,
    inactive: userList?.filter(u => u.is_active === 0)?.length || 0,
  }

  // Count active permissions for badge
  const activePermissionsCount = countEffectivePermissionRoles(
    userPermissionData?.permissionData || [],
    inheritedPermissions,
  )
  const isSuperAdmin = hasAutomaticAdminAccess(selectedUser?.user_role_type)

  // Get user groups count for badge
  const userGroupsCount = useGroupForm.watch('group')?.length || 0

  return (
    <SettingAccessUserPageContext.Provider value={{ uuid: selectedUser?.uuid, setActionLog, defaultValues }}>
      <ActionPaper header={{
        title: 'การจัดการผู้ใช้งาน',
        flexEnd: (
          <AddButton onClick={handleAddUser}>เพิ่มผู้ใช้งาน</AddButton>
        )
      }}>
        <Grid container sx={{ height: '100%', overflow: 'hidden' }}>
          <Grid item xs={12} md={4} lg={3} sx={{ border: 0, height: '100%' }}>
            <GroupPanel title={'ผู้ใช้งาน'} sx={{ border: 0 }}>
              {/* Summary Stats */}
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Box 
                  display="flex" 
                  gap={1} 
                  mb={2}
                >
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      flex: 1, 
                      p: 1.5, 
                      textAlign: 'center', 
                      bgcolor: 'primary.50',
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="h5" fontWeight="bold" color="primary.main">
                      {summaryStats.total}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">ทั้งหมด</Typography>
                  </Paper>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      flex: 1, 
                      p: 1.5, 
                      textAlign: 'center', 
                      bgcolor: 'success.50',
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="h5" fontWeight="bold" color="success.main">
                      {summaryStats.active}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">ใช้งาน</Typography>
                  </Paper>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      flex: 1, 
                      p: 1.5, 
                      textAlign: 'center', 
                      bgcolor: 'grey.100',
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="h5" fontWeight="bold" color="text.secondary">
                      {summaryStats.inactive}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">ไม่ใช้งาน</Typography>
                  </Paper>
                </Box>

                {/* Search */}
                <TextField 
                  size="small" 
                  placeholder="ค้นหาผู้ใช้..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 1 }}
                />
                <ToggleButtonGroup 
                  size="small" 
                  value={statusFilter}
                  exclusive
                  onChange={(e, newValue) => newValue && setStatusFilter(newValue)}
                  fullWidth
                  sx={{
                    '& .MuiToggleButton-root': {
                      fontSize: '0.75rem',
                      py: 0.5
                    }
                  }}
                >
                  <ToggleButton value="all">ทั้งหมด</ToggleButton>
                  <ToggleButton value="active">ใช้งาน</ToggleButton>
                  <ToggleButton value="inactive">ไม่ใช้งาน</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* User List */}
              <Box sx={{ overflow: 'auto', flex: 1 }}>
                {usersQuery.isLoading ? (
                  <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  filteredUsers?.map((user, index) => (
                    <GroupPanelItem 
                      onClick={() => handleSelectedUser(user)} 
                      key={index} 
                      active={selectedUser?.uuid === user.uuid}
                      sx={{
                        transition: 'all 0.2s ease-in-out',
                        cursor: 'pointer',
                        position: 'relative',
                        '&:hover': {
                          transform: 'translateX(4px)',
                          bgcolor: 'action.hover',
                          '& .user-avatar': {
                            transform: 'scale(1.05)',
                          },
                          '& .quick-actions': {
                            opacity: 1,
                            visibility: 'visible',
                          }
                        },
                        '&.active': {
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          '& .MuiChip-root': {
                            bgcolor: 'rgba(255,255,255,0.2)',
                            color: 'inherit'
                          },
                          '& .quick-actions': {
                            '& .MuiIconButton-root': {
                              color: 'primary.contrastText',
                              '&:hover': {
                                bgcolor: 'rgba(255,255,255,0.2)',
                              }
                            }
                          }
                        }
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={2} width="100%">
                        {/* Avatar with online indicator */}
                        <Box position="relative">
                          <Avatar 
                            src={user.avatar} 
                            alt={user.firstname} 
                            className="user-avatar"
                            sx={{
                              width: 44,
                              height: 44,
                              border:'2px solid',
                              borderColor: user.is_active ? 'success.main' : 'grey.400',
                              transition: 'transform 0.2s ease-in-out'
                            }}
                          >
                            {user.firstname?.[0] || user.demo_name?.[0]}
                          </Avatar>
                          {user.uuid && (
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: 0,
                                right: 0,
                                width: 14,
                                height: 14,
                                borderRadius: '50%',
                                bgcolor: user.is_active ? 'success.main' : 'grey.400',
                                border: '2px solid white',
                              }}
                            />
                          )}
                        </Box>
                        <Box flex={1} overflow="hidden">
                          <Typography variant="body2" fontWeight="bold" noWrap>
                            {user.firstname ? `${user.firstname} ${user.lastname || ''}` : user?.demo_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {user.email || user.username}
                          </Typography>
                          <Box display="flex" gap={0.5} mt={0.5} flexWrap="wrap">
	                            {user.user_group?.length > 0 && (
	                              <Chip
	                                icon={<GroupIcon sx={{ fontSize: '0.75rem !important' }} />}
	                                label={`${user.user_group.length} กลุ่ม`}
	                                size="small"
	                                variant="outlined"
	                                sx={{ fontSize: '0.65rem', height: 20 }}
	                              />
	                            )}
	                            {shouldShowCustomerCompanyScopeSummary(user.total_customer_companies) && (
	                              <Chip
	                                icon={<BusinessIcon sx={{ fontSize: '0.75rem !important' }} />}
	                                label={getCustomerCompanyScopeSummaryLabel(user.total_customer_companies)}
	                                size="small"
	                                variant="outlined"
	                                color="primary"
	                                sx={{ fontSize: '0.65rem', height: 20 }}
	                              />
	                            )}
	                          </Box>
                        </Box>
                        
                        {/* Quick Actions */}
                        {user.uuid && (
                          <Box 
                            className="quick-actions"
                            sx={{
                              position: 'absolute',
                              right: 8,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              display: 'flex',
                              gap: 0.5,
                              opacity: 0,
                              visibility: 'hidden',
                              transition: 'opacity 0.2s ease-in-out',
                              bgcolor: 'background.paper',
                              borderRadius: 1,
                              boxShadow: 1,
                              p: 0.5,
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Tooltip title="Copy Email">
                              <IconButton 
                                size="small"
                                onClick={() => {
                                  navigator.clipboard.writeText(user.email || '')
                                }}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                    </GroupPanelItem>
                  ))
                )}
                
                {!usersQuery.isLoading && filteredUsers.length === 0 && (
                  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={4}>
                    <PersonIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      ไม่พบผู้ใช้งาน
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ลองปรับเงื่อนไขการค้นหา
                    </Typography>
                  </Box>
                )}
              </Box>
            </GroupPanel>
          </Grid>
          <Grid item xs={9} sx={{ height: '100%' }}>
            <TabsPanel currentTab={currentTab} onChange={index => { setCurrentTab(index); return true }} sx={{ borderLeft: 0 }} tabs={[
              {
                label: 'ข้อมูลผู้ใช้',
                icon: <PersonIcon />,
                component: isLoadingContent ? <LinearProgress /> : <UserInformation useInfoForm={useInfoForm} />
              },
              {
                label: 'สมาชิกกลุ่ม',
                icon: <GroupIcon />,
                badge: !isSuperAdmin && userGroupsCount > 0 ? userGroupsCount : undefined,
                badgeColor: 'info',
                disabled: !selectedUser?.uuid,
                component: isSuperAdmin ? <SuperAdminAccessPanel section="groups" /> : isLoadingContent ? <LinearProgress /> : <UserMemberManage useGroupForm={useGroupForm} />
              },
              {
                label: 'สิทธิ์การใช้งาน',
                icon: <SecurityIcon />,
                badge: !isSuperAdmin && activePermissionsCount > 0 ? activePermissionsCount : undefined,
                badgeColor: 'success',
                disabled: !selectedUser?.uuid,
                component: isSuperAdmin ? <SuperAdminAccessPanel section="permissions" /> : isLoadingContent ? <LinearProgress /> : (
                  <UserAccessManage
                    usePermissionForm={usePermissionForm}
                    inheritedPermissions={inheritedPermissions}
                    isInheritedLoading={isInheritedLoading}
                  />
                )
              },
              {
                label: 'Customer Scope',
                icon: <BusinessIcon />,
                disabled: !selectedUser?.uuid,
                component: isSuperAdmin ? <SuperAdminAccessPanel section="customer" /> : <CustomerCompanyScopeManage />
              },
              {
                label: 'Transport Scope',
                icon: <LocalShippingIcon />,
                disabled: !selectedUser?.uuid,
                component: isSuperAdmin ? <SuperAdminAccessPanel section="transport" /> : <TransportManagerDcScopeManage />
              },
            ]} />
          </Grid>
        </Grid>
      </ActionPaper>
    </SettingAccessUserPageContext.Provider>

  );
}
